import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import { matchesUrl } from '@findemdev/findem-helpers';
import { BeaconPersonOffLimitResponse, IGetFunctionalAreaHierarchyParams, IGetFunctionalAreaHierarchyResponse, IGetIndustryCategorizationResponse, IGetPersonOffLimitParams } from '../types/beacon';
import {
  CredentialLibraryType,
  ICloneCredentialPayload,
  ICreateCredentialPayload, ICredBuilderFiltersACPayload, ICredBuilderFiltersACResp, ICredBuilderImgUploadPayload, ICredBuilderImgUploadResponse, ICredByRefPayload, ICredential, ICredentialResponse,
  ICredentialResponseRaw,
  ICredentialsResponse, ICredHistoryPayload, IDeleteCredentialPayload, IDeleteCredentialResponse, IDeleteLogoPreferencesPayload, IDeleteLogoPreferencesResponse, IGetAssignmentsPayload,
  IGetLinkedSearchDetailsPayload, IGetLinkedSearchDetailsResponse, IGetLogoPreferencesByIdsPayload, IGetLogoPrerencesByIdsResponse, ILinkBeaconToCredPayload, IRequirement, ISearchAssignmentsPayload,
  ISearchCredsPayload, IToggleCredentialMarquee, IToggleFavoriteCredential, ITransferCredentialOwnershipPayload, IUnlinkBeaconToCredPayload, IUpdateCredentialPayload, IUpdateDefaultLogoPreferenceResponse, IUploadLogoPreferencesPayload, IUploadLogoPreferencesResponse,
  ProjectScope
} from '../components/CredentialApp/types/credential';
import { IProjectsResponse } from '../components/CredentialApp/types/project';
import { IATSJob } from '../types/ats';
import { IGetAtsJobsPayload } from '../components/CredentialApp/types/ats';
import { GLOBAL_REQUIREMENT } from '../components/CredentialApp/util/constants';
import { LEGACY_POSITION_LOCATION_KEY, POSITION_LOCATION_KEY } from '../components/CredentialApp/common/constants/assignment-filters';
// types
import { IUser } from '../types/users';
import { IMacro } from '../types/macros';
import { IUserPreference } from '../models/user';
import { RootState } from '../store';
import { notification } from '@findemdev/maui';
import { TabId } from '../components/CredentialApp/components/List/NavigationTabs';
import { FilterObj, FilterType } from '../components/CredentialApp/components/List/Filters/constants';
import { ACLS } from '../constants/acls';

const DEFAULT_AC_LIMIT = "40";

interface IGetMacroDetailsParams {
  force_refresh?: boolean;
  joid?: string;
  lean?: boolean;
  macro_id: string;
}

export const matchesApi = createApi({
  reducerPath: 'matches',
  tagTypes: ['Credentials', 'CredentialByRef', 'LogoPreferences', 'MacroDetails', 'UserPreferences'],
  baseQuery: fetchBaseQuery({
    baseUrl: matchesUrl
  }),
  endpoints: (build) => ({

    // Credbuilder - start
    createCred: build.mutation<ICredentialResponse, ICreateCredentialPayload>({
      query: ({ name, joid, client_company_name, industry, practice, type, capability, requirements }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            'ref': undefined,
            name,
            client_company_name,
            industry,
            practice,
            type,
            capability,
            requirements,
          }
        }
      }
    }),
    updateCred: build.mutation<ICredentialResponse, IUpdateCredentialPayload>({
      query: ({ joid, credential }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: credential
        }
      }
    }),
    deleteCred: build.mutation<IDeleteCredentialResponse, IDeleteCredentialPayload>({
      invalidatesTags: ['Credentials'],
      query: ({ ref, joid }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'DELETE',
          body: {
            ref
          }
        }
      }
    }),
    transferCredentialOwnership: build.mutation<ICredentialResponse, ITransferCredentialOwnershipPayload>({
      invalidatesTags: ['Credentials'],
      query: ({ ref, from_user, to_user, joid }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder/owner?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            ref,
            from_user,
            to_user
          }
        }
      }
    }),
    linkBeaconToCred: build.mutation<ICredentialResponse, ILinkBeaconToCredPayload>({
      query: ({ ref, project_id, joid }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            ref,
            project: { project_id }
          }
        }
      }
    }),
    unlinkBeaconToCred: build.mutation<ICredentialResponse, IUnlinkBeaconToCredPayload>({
      query: ({ ref, joid }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            ref
          }
        }
      }
    }),
    credByRef: build.query<ICredentialResponse, ICredByRefPayload>({
      providesTags: (result) => result ? [{ type: 'CredentialByRef', id: result.data.ref }] : [],
      query: ({ ref, joid }) => {
        const params = new URLSearchParams({ ref, joid });
        return {
          url: `/hm/api/credbuilder?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'GET',
        }
      },
      transformResponse: (response: ICredentialResponseRaw) => {
        const result = response.data[0];
        result.requirements = result.requirements.map(
          (requirement: IRequirement) => {
            requirement.bool_str = unescape(requirement.bool_str);
            if (requirement.req_cxt?.bool_cxt) {
              requirement.req_cxt.bool_cxt = requirement.req_cxt.bool_cxt.map((bool: string) => unescape(bool));
            }
            /**
             * swap old position location key with the latest
             * https://findemjira.atlassian.net/browse/RRINT-1431
             */
            if (requirement.section_id === LEGACY_POSITION_LOCATION_KEY) {
              requirement.section_id = POSITION_LOCATION_KEY;
            }
            return requirement;
          }
        );
        return { data: result };
      },
      async onQueryStarted(arg, { getState, queryFulfilled, updateCachedData }) {
        try {
          const { data } = await queryFulfilled;
          const joid = data.data.uid;
          const library = data.data.libraries ?? [];
          const state = getState() as RootState;
          const currentUserResult = matchesApi.endpoints.getCurrentUser.select()(state);
          const currentUserId = currentUserResult?.data?.uid;
          const hasReadPermissions = currentUserId === joid ||
            currentUserResult?.data?.roles.includes(ACLS.FM_CRED_ADMIN_ACL) ||
            library.includes(CredentialLibraryType.CRED_LIBRARY) ||
            library.includes(CredentialLibraryType.MARQUEE_LIBRARY);

          if (!hasReadPermissions) {
            updateCachedData((draft) => {
              draft.data = {} as ICredential;
            });
            notification.error({
              message: 'Access Denied',
              description: 'You do not have permission to view this credential.'
            });
          }
        } catch (error) {
          console.error('Query failed:', error);
        }
      }
    }),
    searchCreds: build.query<ICredentialsResponse, ISearchCredsPayload>({
      providesTags: (result) => result ? [{ type: 'Credentials' }] : [],
      query: ({ page, size, filters, joid, sort_fields, activeTab }) => {
        const params = new URLSearchParams({ joid });
        let projectScope: ProjectScope = ProjectScope.PERSONAL;
        const isFavFilter = filters && filters.some(filter => filter.field === FilterObj[FilterType.FAVORITE].filterKeyword);
        const isFavFilterInSharedTab = ((activeTab === TabId['cred-library'] || activeTab === TabId['marquees']) && isFavFilter);

        // in favorites tab, by default show all favorites, whereas for other tabs show all if the favorite filter is applied
        if ((activeTab === TabId['favorites'] || isFavFilterInSharedTab)) {
          projectScope = ProjectScope.ALL;
        }
        return {
          url: `/hm/api/credbuilder/search?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            page,
            size,
            project_scope: projectScope,
            ...(filters && { filters }),
            ...(sort_fields && { sort_fields }),
          }
        }
      }
    }),
    credHistory: build.query<ICredentialsResponse, ICredHistoryPayload>({
      query: ({ ref, joid }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder/history?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            ref
          }
        }
      }
    }),
    searchAssignments: build.query<IProjectsResponse, ISearchAssignmentsPayload>({
      query: ({ page, size, project_ref, refs, requirements, shortlist, joid, search_id }) => {
        const params = new URLSearchParams({ joid });
        const sanitizedRequirements = requirements?.map((requirement) => ({
          ...requirement,
          bool_str: unescape(requirement.bool_str),
          req_cxt: requirement.req_cxt?.bool_cxt
            ? {
                ...requirement.req_cxt,
                bool_cxt: requirement.req_cxt.bool_cxt.map((bool: string) => unescape(bool)),
              }
            : requirement.req_cxt,
        }));

        return {
          url: `/hm/api/assignment/search?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            page,
            size,
            requirements: [
              ...GLOBAL_REQUIREMENT,
              ...(sanitizedRequirements ?? []),
            ],
            ...(search_id && { search_id }),
            ...(project_ref && { project_ref }),
            ...(shortlist && { shortlist }),
            ...(refs && { refs })
          }
        }
      }
    }),
    getAssignments: build.query<IProjectsResponse, IGetAssignmentsPayload>({
      query: ({ page, size, refs, joid, requirements }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `/hm/api/assignment/search?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            page,
            size,
            requirements,
            ...(refs && { refs })
          }
        }
      },
      transformResponse: (response: IProjectsResponse, meta, arg) => {
        // make the order of the refs in the response the same as the refs passed in the payload
        // BE doesnt follow the order of the refs passed in the payload in the response data
        const sortedData = response.data.sort((a, b) => {
          return arg.refs.indexOf(a.ref) - arg.refs.indexOf(b.ref);
        });

        return { ...response, data: sortedData };
      }
    }),
    getAtsJobs: build.query<IATSJob[], IGetAtsJobsPayload>({
      query: ({ joid }) => {
        const params = new URLSearchParams({ joid, is_rra: "true" });

        return {
          url: `/hm/api/ats-jobs?${params.toString()}`,
          credentials: 'include'
        }
      },
    }),
    getCredBuilderFiltersAC: build.query<ICredBuilderFiltersACResp, ICredBuilderFiltersACPayload>({
      query: (params: ICredBuilderFiltersACPayload) => {
        const searchParams = new URLSearchParams({
          field: params.field,
          prefix: params.prefix,
          joid: params.joid,
          limit: DEFAULT_AC_LIMIT
        });

        return {
          url: `/hm/api/credbuilder/autocomplete?${searchParams.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
      }
    }),
    getAssignmentsFieldsAC: build.query<ICredBuilderFiltersACResp, ICredBuilderFiltersACPayload>({
      query: (params: ICredBuilderFiltersACPayload) => {
        const searchParams = new URLSearchParams({
          field: params.field,
          prefix: params.prefix,
          joid: params.joid,
          limit: DEFAULT_AC_LIMIT
        });

        return {
          url: `/hm/api/assignment/autocomplete?${searchParams.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
      }
    }),
    // Credbuilder - Logos - start
    uploadImg: build.mutation<ICredBuilderImgUploadResponse, ICredBuilderImgUploadPayload>({
      query: ({ joid, formData }) => {
        const searchParams = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder/img_upload?${searchParams.toString()}`,
          credentials: 'include',
          method: 'POST',
          body: formData
        }
      }
    }),
    getLogoPreferencesByIds: build.query<IGetLogoPrerencesByIdsResponse, IGetLogoPreferencesByIdsPayload>({
      providesTags: ['LogoPreferences'],
      query: ({ joid, ids }) => {
        const searchParams = new URLSearchParams({
          joid,
          client_company_ids: ids.join(",")
        });

        return {
          url: `/hm/api/credbuilder/logo_preferences?${searchParams.toString()}`,
          credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      },
    }),
    uploadLogoPreference: build.mutation<IUploadLogoPreferencesResponse, IUploadLogoPreferencesPayload>({
      invalidatesTags: ['LogoPreferences'],
      query: ({ joid, id, logoUrl }) => {
        const searchParams = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder/logo_preferences?${searchParams.toString()}`,
          credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            client_company_id: id,
            logo_url: logoUrl,
          },
        }
      }
    }),
    updateDefaultLogoPreference: build.mutation<IUpdateDefaultLogoPreferenceResponse, IUploadLogoPreferencesPayload>({
      invalidatesTags: ['LogoPreferences'],
      query: ({ joid, id, logoUrl }) => {
        const searchParams = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder/logo_preferences/default?${searchParams.toString()}`,
          credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            client_company_id: id,
            logo_url: logoUrl,
          }
        }
      }
    }),
    deleteLogoPreference: build.mutation<IDeleteLogoPreferencesResponse, IDeleteLogoPreferencesPayload>({
      invalidatesTags: ['LogoPreferences'],
      query: ({ joid, id, logos }) => {
        const searchParams = new URLSearchParams({ joid });

        return {
          url: `/hm/api/credbuilder/logo_preferences?${searchParams.toString()}`,
          credentials: 'include',
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            client_company_id: id,
            logo_urls_to_delete: logos,
            force: true, // will delete even if there wont be any remaining logo after the delete
          }
        }
      }
    }),
    // Credbuilder - Logos - end
    getLinkedSearchDetails: build.query<IGetLinkedSearchDetailsResponse, IGetLinkedSearchDetailsPayload>({
      query: (params: IGetLinkedSearchDetailsPayload) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
          refs: params.refs.join(",")
        });

        return {
          url: `/hm/api/assignment/icp?${searchParams.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
      }
    }),
    toggleFavoriteCredential: build.mutation<ICredentialResponse, IToggleFavoriteCredential>({
      invalidatesTags: ['Credentials'],
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        return {
          url: `hm/api/credbuilder/favorite?${searchParams.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            ref: params.ref,
          }
        }
      }
    }),
    cloneCredential: build.mutation<ICredentialResponse, ICloneCredentialPayload>({
      invalidatesTags: ['Credentials'],
      query: ({ joid, ref, name, }) => {
        const params = new URLSearchParams({ joid, });

        return {
          url: `/hm/api/credbuilder?${params.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            parent_id: ref,
            name,
          }
        }
      }
    }),
    toggleMarqueeCredential: build.mutation<ICredentialResponse, IToggleCredentialMarquee>({
      invalidatesTags: ['Credentials'],
      query: ({ joid, library, ref, }) => {
        const searchParams = new URLSearchParams({
          joid,
        });

        // if marquee library is present, remove it else add it
        const newLibrary = library.includes(CredentialLibraryType.MARQUEE_LIBRARY) ?
          library.filter((lib) => lib !== CredentialLibraryType.MARQUEE_LIBRARY) :
          [...(library ?? []), CredentialLibraryType.MARQUEE_LIBRARY];

        return {
          url: `/hm/api/credbuilder?${searchParams.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: {
            ref,
            libraries: Array.from(new Set(newLibrary)),
          }
        }
      }
    }),
    // Credbuilder - end

    // RRA - start
    getPersonOffLimit: build.query<BeaconPersonOffLimitResponse, IGetPersonOffLimitParams>({
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
          edm_person_id: params.edm_person_id
        });

        return {
          url: `hm/api/rra/person_off_limit?${searchParams.toString()}`,
          credentials: 'include',
          method: 'GET',
        }
      }
    }),
    getFunctionalAreaHierarchy: build.query<IGetFunctionalAreaHierarchyResponse, IGetFunctionalAreaHierarchyParams>({
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        return {
          url: `hm/api/credbuilder/functional_area_hierarchies?${searchParams.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'GET',
        };
      }
    }),
    getIndustryCategorization: build.query<IGetIndustryCategorizationResponse, void>({
      query: () => ({
        url: 'hm/api/credbuilder/industry_categorization',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'GET',
      }),
    }),
    // RRA - end

    // User - start
    getCurrentUser: build.query<IUser, void>({
      query: () => ({
        url: '/api/user_info',
        credentials: 'include',
        method: 'GET',
      }),
    }),
    getUserPreferences: build.query<IUserPreference, { joid: string }>({
      providesTags: ['UserPreferences'],
      query: ({ joid }) => ({
        url: `/hm/api/user/pref?joid=${joid}`,
        credentials: 'include',
        method: 'GET',
      }),
      transformResponse: (response: { user_pref: IUserPreference }) => {
        return response.user_pref;
      }
    }),
    getMacroDetails: build.query<IMacro, IGetMacroDetailsParams>({
      query: ({ force_refresh = false, joid, lean = true, macro_id }) => ({
        url: `/hm/api/macro${joid ? `?${new URLSearchParams({ joid }).toString()}` : ''}`,
        credentials: 'include',
        method: 'POST',
        body: {
          force_refresh,
          joid,
          lean,
          macro_id,
          type: 'MacroDetails',
        },
      }),
      providesTags: (_result, _error, { macro_id }) => [
        { type: 'MacroDetails', id: macro_id },
      ],
      transformResponse: (response: { macro?: IMacro } | IMacro) => {
        if ('macro' in response && response.macro) return response.macro;

        return response as IMacro;
      },
    }),
    updateUserAppPrefs: build.mutation<IUserPreference, { uid: string; apps: string[] }>({
      invalidatesTags: ['UserPreferences'],
      query: ({ uid, apps }) => ({
        url: '/hm/api/user/pref',
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          uid,
          apps,
        },
      }),
    }),
    // User - end
  })
});

// selectors
export const selectAddRemoveAssignmentMutationStatus = matchesApi.endpoints.updateCred.select('add-remove-assginment');
export const selectIsToggleFavoriteCredPending = matchesApi.endpoints.toggleFavoriteCredential.select('favorite-toggle-creds-list');

export const {
  endpoints,

  // Credbuilder - start
  useCreateCredMutation,
  useUpdateCredMutation,
  useDeleteCredMutation,
  useTransferCredentialOwnershipMutation,
  useLinkBeaconToCredMutation,
  useUnlinkBeaconToCredMutation,
  useCredByRefQuery,
  useSearchCredsQuery,
  useCredHistoryQuery,
  useSearchAssignmentsQuery,
  useLazySearchAssignmentsQuery,
  useGetAssignmentsQuery,
  useGetAtsJobsQuery,
  useLazyGetCredBuilderFiltersACQuery,
  useLazyGetAssignmentsFieldsACQuery,
  useCloneCredentialMutation,
  useToggleMarqueeCredentialMutation,
  // Credbuilder - Logos - start
  useUploadImgMutation,
  useGetLogoPreferencesByIdsQuery,
  useUploadLogoPreferenceMutation,
  useUpdateDefaultLogoPreferenceMutation,
  useDeleteLogoPreferenceMutation,
  // Credbuilder - Logos - end
  useGetLinkedSearchDetailsQuery,
  useToggleFavoriteCredentialMutation,
  // Credbuilder - end

  // RRA - start
  useLazyGetPersonOffLimitQuery,
  useGetFunctionalAreaHierarchyQuery,
  useLazyGetFunctionalAreaHierarchyQuery,
  useGetIndustryCategorizationQuery,
  useLazyGetIndustryCategorizationQuery,
  // RRA - end

  // User - start
  useGetCurrentUserQuery,
  useGetMacroDetailsQuery,
  useLazyGetMacroDetailsQuery,
  useGetUserPreferencesQuery,
  useUpdateUserAppPrefsMutation,
  // User - end
} = matchesApi;
